import { useEffect, useState, useCallback } from "react";
import { Link, useNavigate } from "react-router-dom";
import API from "../../../../services/api";
import {
  Typography,
  Spin,
  Button,
  message,
  Empty,
  Tag,
  Space,
  Popconfirm,
  Pagination,
  Select,
  Modal,
} from "antd";
import {
  PlusOutlined,
  EditOutlined,
  EyeOutlined,
  EnvironmentOutlined,
  AreaChartOutlined,
  DeleteOutlined,
} from "@ant-design/icons";
import "./MyCommercialSpaces.css";

const { Text, Paragraph } = Typography;

function MyCommercialSpaces() {
  const [spaces, setSpaces] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [fetching, setFetching] = useState(false);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [actionLoadingId, setActionLoadingId] = useState(null);
  const [filter, setFilter] = useState("all");
  const [stats, setStats] = useState({
    total: 0,
    activeCount: 0,
    pendingCount: 0,
    totalRevenue: 0,
  });
  const [isMobileFilter, setIsMobileFilter] = useState(
    typeof window !== "undefined" ? window.innerWidth <= 460 : false
  );
  const navigate = useNavigate();

  const fetchMySpaces = useCallback(async (pageNumber = 1) => {
    if (fetching) return;

    try {
      setFetching(true);
      setLoading(true);

      const res = await API.get(
        `/commercial-spaces/my/listings?page=${pageNumber}&filter=${filter}`
      );

      setSpaces(res.data.listings || []);
      setTotalPages(res.data.totalPages || 1);
      setTotal(res.data.total || 0);
      setStats(res.data.stats || {});
    } catch (err) {
      console.error("Fetch error:", err);
    } finally {
      setLoading(false);
      setFetching(false);
    }
  }, [fetching, filter]);

  useEffect(() => {
    setPage(1);
    fetchMySpaces(1);
  }, [filter]);

  useEffect(() => {
    fetchMySpaces(page);
  }, [page]);

  useEffect(() => {
    const handleResize = () => {
      setIsMobileFilter(window.innerWidth <= 460);
    };

    handleResize();
    window.addEventListener("resize", handleResize);

    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, []);

  const handleStatusToggle = async (id, currentStatus) => {
    try {
      setActionLoadingId(id);
      const endpoint = currentStatus === "active" ? "/deactivate" : "/activate";
      await API.put(`/commercial-spaces/${id}${endpoint}`);
      message.success(currentStatus === "active" ? "Deactivated" : "Activated");
      fetchMySpaces(page);
    } catch (err) {
      message.error("Status update failed");
    } finally {
      setActionLoadingId(null);
    }
  };

  const handleDelete = (id, title) => {
    Modal.confirm({
      title: "Delete Listing",
      content: `Are you sure you want to delete "${title}"? This action cannot be undone.`,
      okText: "Delete",
      okButtonProps: { danger: true },
      cancelText: "Cancel",
      onOk: async () => {
        try {
          await API.delete(`/commercial-spaces/${id}`);
          message.success("Listing deleted successfully");
          setSpaces((prev) => prev.filter((s) => s._id !== id));
        } catch (err) {
          message.error("Failed to delete listing");
        }
      },
    });
  };

  const formatPrice = (price, type) => {
    if (!price) return "Negotiable";
    return `₹${Number(price).toLocaleString("en-IN")}${type === "rent" ? "/mo" : "/yr"}`;
  };

  const getStatusConfig = (status) => ({
    active: { color: "#10b981", bg: "#ecfdf5", label: "Active" },
    inactive: { color: "#6b7280", bg: "#f3f4f6", label: "Inactive" }
  })[status] || { color: "#ef4444", bg: "#fef2f2", label: "Error" };

  const getApprovalConfig = (status) => ({
    approved: { color: "#3b82f6", bg: "#eff6ff", label: "Approved" },
    pending: { color: "#f59e0b", bg: "#fef3c7", label: "Pending" },
    rejected: { color: "#ef4444", bg: "#fef2f2", label: "Rejected" }
  })[status] || { color: "#6b7280", bg: "#f3f4f6", label: "Unknown" };

  if (loading) {
    return (
      <div className="cmsp-page">
        <div className="cmsp-shell">
          <div className="cmsp-loading">
            <Spin size="large" />
          </div>
        </div>
      </div>
    );
  }

  const activeCount = stats.activeCount;
  const pendingCount = stats.pendingCount;
  const totalRevenue = stats.totalRevenue;

  const filteredSpaces = spaces;

  const filterOptions = [
    { label: "All", value: "all" },
    { label: "Approved", value: "approved" },
    { label: "Rejected", value: "rejected" },
    { label: "Active", value: "active" },
    { label: "Inactive", value: "inactive" },
  ];

  return (
    <div className="cmsp-page">
      <div className="cmsp-shell">
        <section className="cmsp-overview">
          <div className="cmsp-overview__stats">
            <div className="cmsp-overview__stat">
              <div className="cmsp-overview__value">{stats.total}</div>
              <div className="cmsp-overview__label">Total Listings</div>
            </div>

            <div className="cmsp-overview__stat">
              <div className="cmsp-overview__value cmsp-overview__value--active">
                {activeCount}
              </div>
              <div className="cmsp-overview__label">Active</div>
            </div>

            <div className="cmsp-overview__stat">
              <div className="cmsp-overview__value cmsp-overview__value--pending">
                {pendingCount}
              </div>
              <div className="cmsp-overview__label">Pending Review</div>
            </div>

            <div className="cmsp-overview__stat">
              <div className="cmsp-overview__value cmsp-overview__value--revenue">
                ₹{totalRevenue.toLocaleString()}
              </div>
              <div className="cmsp-overview__label">Est. Value</div>
            </div>
          </div>

          <Link to="/add-commercial-space" className="cmsp-overview__cta">
            <Button size="large" type="primary" icon={<PlusOutlined />}>
              Create New Space
            </Button>
          </Link>
        </section>

        <section className="cmsp-toolbar">
          <div className="cmsp-toolbar__left">
            <div className="cmsp-toolbar__title-wrap">
              <Text className="cmsp-toolbar__title">My Listings</Text>
              <Text className="cmsp-toolbar__subtitle">
                Manage all your commercial space listings in one place
              </Text>
            </div>
          </div>

          <div className="cmsp-toolbar__right">
            {isMobileFilter ? (
              <Select
                value={filter}
                onChange={setFilter}
                options={filterOptions}
                className="cmsp-filters__select"
                classNames={{ popup: { root: "cmsp-filters__dropdown" } }}
                size="middle"
              />
            ) : (
              <div className="cmsp-filters">
                <Space wrap size={[10, 10]}>
                  <Button
                    type={filter === "all" ? "primary" : "default"}
                    onClick={() => setFilter("all")}
                  >
                    All
                  </Button>
                  <Button
                    type={filter === "approved" ? "primary" : "default"}
                    onClick={() => setFilter("approved")}
                  >
                    Approved
                  </Button>
                  <Button
                    type={filter === "rejected" ? "primary" : "default"}
                    onClick={() => setFilter("rejected")}
                  >
                    Rejected
                  </Button>
                  <Button
                    type={filter === "active" ? "primary" : "default"}
                    onClick={() => setFilter("active")}
                  >
                    Active
                  </Button>
                  <Button
                    type={filter === "inactive" ? "primary" : "default"}
                    onClick={() => setFilter("inactive")}
                  >
                    Inactive
                  </Button>
                </Space>
              </div>
            )}
          </div>
        </section>

        {spaces.length === 0 ? (
          <div className="cmsp-empty">
            <Empty
              image={Empty.PRESENTED_IMAGE_SIMPLE}
              description={
                <div>
                  <Text strong className="cmsp-empty__title">
                    No listings yet
                  </Text>
                  <Paragraph className="cmsp-empty__text">
                    Create your first commercial space listing to get started
                  </Paragraph>
                </div>
              }
            >
              <Link to="/add-commercial-space">
                <Button type="primary" size="large">
                  Create New Listing
                </Button>
              </Link>
            </Empty>
          </div>
        ) : (
          <section className="cmsp-grid">
            {filteredSpaces.map((space) => (
              <article className="cmsp-card" key={space._id}>
                <div className="cmsp-card__media">
                  <img
                    src={space.thumbnail || "/no-image.png"}
                    alt={space.title}
                    className="cmsp-card__image"
                  />

                  <div className="cmsp-card__badges">
                    <Tag
                      className="cmsp-card__badge"
                      style={{
                        background: getStatusConfig(space.status).bg,
                        color: getStatusConfig(space.status).color,
                        borderColor: "transparent",
                      }}
                    >
                      {getStatusConfig(space.status).label}
                    </Tag>
                    <Tag
                      className="cmsp-card__badge"
                      style={{
                        background: getApprovalConfig(space.approvalStatus).bg,
                        color: getApprovalConfig(space.approvalStatus).color,
                        borderColor: "transparent",
                      }}
                    >
                      {getApprovalConfig(space.approvalStatus).label}
                    </Tag>
                    <Button
                      type="text"
                      size="small"
                      icon={<DeleteOutlined />}
                      onClick={() => handleDelete(space._id, space.title)}
                      className="cmsp-card__delete-btn"
                      danger
                    />
                  </div>
                </div>

                <div className="cmsp-card__body">
                  <Text className="cmsp-card__title" title={space.title}>
                    {space.title}
                  </Text>

                  <div className="cmsp-card__price">
                    <Text className="cmsp-card__price-value">
                      {formatPrice(space.pricing?.price, space.pricing?.type)}
                    </Text>
                  </div>

                  <div className="cmsp-card__meta">
                    <div className="cmsp-card__meta-item">
                      <EnvironmentOutlined className="cmsp-card__meta-icon" />
                      <span>{space.location?.city || "N/A"}</span>
                    </div>

                    <div className="cmsp-card__meta-item">
                      <AreaChartOutlined className="cmsp-card__meta-icon" />
                      <span>{space.propertyDetails?.areaSqFt || 0} sqft</span>
                    </div>
                  </div>

                  <div className="cmsp-card__footer">
                    <div className="cmsp-card__actions">
                      <Button
                        size="small"
                        icon={<EditOutlined />}
                        onClick={() => navigate(`/edit-commercial-space/${space._id}`)}
                        className="cmsp-btn cmsp-btn--secondary"
                      >
                        Edit
                      </Button>

                      <Popconfirm
                        title={`Mark as ${space.status === "active" ? "Inactive" : "Active"}`}
                        description="This will update visibility immediately."
                        onConfirm={() => handleStatusToggle(space._id, space.status)}
                        okText="Confirm"
                        cancelText="Cancel"
                        okButtonProps={{ danger: space.status === "active" }}
                      >
                        <Button
                          size="small"
                          loading={actionLoadingId === space._id}
                          className={`cmsp-btn ${space.status === "active"
                            ? "cmsp-btn--active"
                            : "cmsp-btn--inactive"
                            }`}
                        >
                          {space.status === "active" ? "Deactivate" : "Activate"}
                        </Button>
                      </Popconfirm>

                      <Link
                        to={`/commercial-space/${space.slug}`}
                        className="cmsp-card__view-link"
                      >
                        <Button
                          size="small"
                          icon={<EyeOutlined />}
                          className="cmsp-btn cmsp-btn--primary"
                        >
                          View
                        </Button>
                      </Link>
                    </div>
                  </div>
                </div>
              </article>
            ))}
          </section>
        )}

        {total > 0 && (
          <div className="cmsp-pagination">
            <Pagination
              current={page}
              total={total}
              pageSize={15}
              onChange={(p) => setPage(p)}
              showSizeChanger={false}
              responsive
            />
          </div>
        )}
      </div>
    </div>
  );
}

export default MyCommercialSpaces;